import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/baseUi/accordion"
import { Button } from "@/components/ui/baseUi/button"
import { Text } from "@/components/ui/baseUi/text"
import { ChronoSettingsAMRAPForm } from "@/modules/chrono/components/chronoSettingsAMRAPForm"
import { ChronoSettingsEMOMForm } from "@/modules/chrono/components/chronoSettingsEMOMForm"
import { ChronoSettingsForTimeForm } from "@/modules/chrono/components/chronoSettingsForTimeForm"
import { ChronoSettingsTABATAForm } from "@/modules/chrono/components/chronoSettingsTABATAForm"
import { cn } from "@/modules/lib/utils"
import type { ExerciseFormViewEnum } from "@/modules/sessions/sessionForm/blocks/exerciseBlock/components/exerciseForm"
import {
  type ExerciseFormikType,
  type ExerciseInputKey,
} from "@/modules/sessions/sessionForm/blocks/exerciseBlock/exerciseFormikTypes"
import type { SimpleFormik } from "@/typings/formik"
import { AmrapIcon } from "@images/svgIcons/amrapIcon"
import { EmomIcon } from "@images/svgIcons/emomIcon"
import { ForTimeIcon } from "@images/svgIcons/forTimeIcon"
import { TabataIcon } from "@images/svgIcons/tabataIcon"
import { ChronoType } from "@modules/chrono/chronoTypes"
import { word } from "@modules/core/utils/i18n"

export function ModelChrono({
  formik,
  setCurrentView,
  className,
  ...props
}: {
  formik: SimpleFormik<ExerciseFormikType, ExerciseInputKey>
  setCurrentView: (view: ExerciseFormViewEnum) => void
} & React.ComponentProps<"div">) {
  const setChronoFormik = async (chronoType: ChronoType) => {
    if (
      formik.errors &&
      ((chronoType === ChronoType.EMOM && !!formik.errors.chronoEmom) ||
        (chronoType === ChronoType.AMRAP && !!formik.errors.chronoAmrap) ||
        (chronoType === ChronoType.TABATA && !!formik.errors.chronoTabata) ||
        (chronoType === ChronoType.FOR_TIME && !!formik.errors.chronoForTime))
    ) {
      return
    }

    if (chronoType === ChronoType.AMRAP) {
      await formik.setFieldValue("chrono", formik.values.chronoAmrap)
      await formik.setFieldValue("chronoEmom", undefined)
      await formik.setFieldValue("chronoTabata", undefined)
      await formik.setFieldValue("chronoForTime", undefined)
    }
    if (chronoType === ChronoType.EMOM) {
      await formik.setFieldValue("chrono", formik.values.chronoEmom)
      await formik.setFieldValue("chronoAmrap", undefined)
      await formik.setFieldValue("chronoTabata", undefined)
      await formik.setFieldValue("chronoForTime", undefined)
    }
    if (chronoType === ChronoType.TABATA) {
      await formik.setFieldValue("chrono", formik.values.chronoTabata)
      await formik.setFieldValue("chronoAmrap", undefined)
      await formik.setFieldValue("chronoEmom", undefined)
      await formik.setFieldValue("chronoForTime", undefined)
    }
    if (chronoType === ChronoType.FOR_TIME) {
      await formik.setFieldValue("chrono", formik.values.chronoForTime)
      await formik.setFieldValue("chronoAmrap", undefined)
      await formik.setFieldValue("chronoEmom", undefined)
      await formik.setFieldValue("chronoTabata", undefined)
    }

    await formik.setFieldValue("chronoType", chronoType)

    setCurrentView("exerciseContent")
  }

  return (
    <div className={cn("flex flex-col", className)} {...props}>
      <Accordion className="flex flex-col gap-4">
        <AccordionItem value="amrap" className="rounded-3xl border-none bg-surface-foreground p-4 shadow-none">
          <AccordionTrigger>
            <div className="flex flex-row items-center gap-4">
              <AmrapIcon color={"white"} />
              <div className="flex flex-col gap-1">
                <Text fontStyle="LL">{word("chrono.title.amrap")}</Text>
                <Text fontStyle="BS" color="text-textDefault-caption">
                  {word("chrono.amrap.definition")}
                </Text>
              </div>
            </div>
          </AccordionTrigger>
          <AccordionContent className="flex flex-col gap-2">
            <ChronoSettingsAMRAPForm formik={formik} />

            <Button
              variant="primary"
              size="lg"
              className="mr-2 w-full"
              onClick={() => {
                void setChronoFormik(ChronoType.AMRAP)
              }}
            >
              {word("chrono.button.validate")}
            </Button>
          </AccordionContent>
        </AccordionItem>
        <AccordionItem value="emom" className="rounded-3xl border-none bg-surface-foreground p-4 shadow-none">
          <AccordionTrigger>
            <div className="flex flex-row items-center gap-4">
              <EmomIcon color={"white"} />
              <div className="flex flex-col gap-1">
                <Text fontStyle="LL">{word("chrono.title.emom")}</Text>
                <Text fontStyle="BS" color="text-textDefault-caption">
                  {word("chrono.emom.definition")}
                </Text>
              </div>
            </div>
          </AccordionTrigger>
          <AccordionContent className="flex flex-col gap-2">
            <ChronoSettingsEMOMForm formik={formik} />

            <Button
              variant="primary"
              size="lg"
              className="mr-2 w-full"
              onClick={() => {
                void setChronoFormik(ChronoType.EMOM)
              }}
            >
              {word("chrono.button.validate")}
            </Button>
          </AccordionContent>
        </AccordionItem>
        <AccordionItem value="tabata" className="rounded-3xl border-none bg-surface-foreground p-4 shadow-none">
          <AccordionTrigger>
            <div className="flex flex-row items-center gap-4">
              <TabataIcon color={"white"} />
              <div className="flex flex-col gap-1">
                <Text fontStyle="LL">{word("chrono.title.tabata")}</Text>
                <Text fontStyle="BS" color="text-textDefault-caption">
                  {word("chrono.tabata.definition")}
                </Text>
              </div>
            </div>
          </AccordionTrigger>
          <AccordionContent className="flex flex-col gap-2">
            <ChronoSettingsTABATAForm formik={formik} />
            <Button
              variant="primary"
              size="lg"
              className="mr-2 w-full"
              onClick={() => {
                void setChronoFormik(ChronoType.TABATA)
              }}
            >
              {word("chrono.button.validate")}
            </Button>
          </AccordionContent>
        </AccordionItem>
        <AccordionItem value="forTime" className="rounded-3xl border-none bg-surface-foreground p-4 shadow-none">
          <AccordionTrigger>
            <div className="flex flex-row items-center gap-4">
              <ForTimeIcon color={"white"} />
              <div className="flex flex-col gap-1">
                <Text fontStyle="LL">{word("chrono.title.for_time")}</Text>
                <Text fontStyle="BS" color="text-textDefault-caption">
                  {word("chrono.forTime.definition")}
                </Text>
              </div>
            </div>
          </AccordionTrigger>
          <AccordionContent className="flex flex-col gap-2">
            <ChronoSettingsForTimeForm formik={formik} />
            <Button
              variant="primary"
              size="lg"
              className="mr-2 w-full"
              onClick={() => {
                void setChronoFormik(ChronoType.FOR_TIME)
              }}
            >
              {word("chrono.button.validate")}
            </Button>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  )
}
